import React, { useEffect, useState } from "react";
import { hasRole } from "../../lib/roles";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Youtube, BookOpen, Save, Sparkles, MessageCircle } from "lucide-react";
import ImageUploader from "../../components/ImageUploader";
import { ytEmbed } from "../../lib/constants";
import { useAuth } from "../../lib/auth";

/**
 * Home Video & Our Story — Super Admin content controls.
 *
 * Both tasks are delegatable: a Major Admin who has been granted the
 * "home_video" / "our_story" feature from Feature Toggles sees the same
 * editors. The backend enforces this independently.
 */
export default function HomeAndStoryPage() {
  const { user } = useAuth();
  const isSuper = hasRole(user, "super_admin");
  const granted = new Set(user?.features || []);
  const canVideo = isSuper || granted.has("*") || granted.has("home_video");
  const canStory = isSuper || granted.has("*") || granted.has("our_story");
  // The home ribbon is routine merchandising — Major Admin / Admin may edit it.
  const canScheme = isSuper || hasRole(user, ["major_admin", "admin"]) || granted.has("*") || granted.has("home_scheme");
  // WhatsApp quick questions — Super Admin and Major Admin only
  const canWa = isSuper || hasRole(user, "major_admin");

  return (
    <div className="p-6 lg:p-8 space-y-10 max-w-4xl">
      <div>
        <div className="eyebrow">Site Content</div>
        <h1 className="font-display text-4xl text-maroon">Home Video &amp; Our Story</h1>
        <p className="text-xs text-ash mt-1">
          Changes go live on the storefront immediately — no redeploy needed.
        </p>
      </div>

      {canVideo ? <HomeVideoEditor /> : <Denied what="the home page video" />}
      {canScheme ? <HomeSchemeEditor /> : <Denied what="the home scheme section" />}
      {canWa && <WhatsAppQuestionsEditor />}
      {canStory ? <OurStoryEditor /> : <Denied what="the Our Story page" />}
    </div>
  );
}

const Denied = ({ what }) => (
  <div className="bg-white border border-line p-6 text-sm text-ash">
    You don't have permission to edit {what}. Ask the Super Admin to delegate it from Feature Toggles.
  </div>
);

/* ---------------- Home page YouTube video ---------------- */
function HomeVideoEditor() {
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/site-settings").then((r) => setUrl(r.data?.showroom_video_url || "")).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (url.trim() && !ytEmbed(url)) {
      toast.error("That doesn't look like a YouTube link. Paste the full video URL.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/crm/site-settings/home-video", { showroom_video_url: url.trim() });
      toast.success("Home page video updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const preview = ytEmbed(url);

  return (
    <form onSubmit={save} className="bg-white border border-line p-6 space-y-4" data-testid="home-video-editor">
      <div className="flex items-center gap-2">
        <Youtube size={18} className="text-maroon" />
        <h2 className="font-display text-2xl text-maroon">Home Page Video</h2>
      </div>
      <div>
        <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">YouTube link</label>
        <input
          className="w-full border border-line px-3 py-2 bg-pearl"
          placeholder="https://www.youtube.com/watch?v=..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          data-testid="home-video-url"
        />
        <p className="text-[11px] text-ash mt-1">
          Any YouTube format works — watch link, youtu.be short link, Shorts or embed.
          Leave blank to hide the video.
        </p>
      </div>

      {preview && (
        <div>
          <div className="text-[10px] text-ash uppercase tracking-widest mb-1">Preview</div>
          <div className="aspect-video max-w-md border border-line">
            <iframe src={preview} title="Preview" allowFullScreen className="w-full h-full" data-testid="home-video-preview"/>
          </div>
        </div>
      )}

      <button className="btn-maroon inline-flex items-center gap-2" disabled={saving} data-testid="home-video-save">
        <Save size={14}/> {saving ? "Saving…" : "Save Video Link"}
      </button>
    </form>
  );
}

/* ---------------- Our Story page ---------------- */
const BLANK_BLOCK = { type: "paragraph", text: "", image_url: "", caption: "" };

function OurStoryEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/crm/site-content/our-story")
      .then((r) => setContent({ eyebrow: "", title: "", hero_image_url: "", blocks: [], ...(r.data || {}) }))
      .catch(() => setContent({ eyebrow: "", title: "", hero_image_url: "", blocks: [] }));
  }, []);

  if (!content) return <div className="bg-white border border-line p-6 text-sm text-ash">Loading…</div>;

  const setBlock = (i, patch) => {
    const blocks = content.blocks.map((b, idx) => idx === i ? { ...b, ...patch } : b);
    setContent({ ...content, blocks });
  };
  const addBlock = (type) => setContent({ ...content, blocks: [...content.blocks, { ...BLANK_BLOCK, type }] });
  const removeBlock = (i) => setContent({ ...content, blocks: content.blocks.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= content.blocks.length) return;
    const blocks = [...content.blocks];
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    setContent({ ...content, blocks });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/crm/site-content/our-story", {
        eyebrow: content.eyebrow || "",
        title: content.title || "",
        hero_image_url: content.hero_image_url || "",
        blocks: content.blocks.map((b) => ({
          type: b.type || "paragraph",
          text: b.text || "",
          image_url: b.image_url || "",
          caption: b.caption || "",
        })),
      });
      toast.success("Our Story page updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="bg-white border border-line p-6 space-y-5" data-testid="our-story-editor">
      <div className="flex items-center gap-2">
        <BookOpen size={18} className="text-maroon" />
        <h2 className="font-display text-2xl text-maroon">Our Story Page</h2>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Eyebrow (small text above title)</label>
          <input className="w-full border border-line px-3 py-2 bg-pearl" value={content.eyebrow}
            onChange={(e) => setContent({ ...content, eyebrow: e.target.value })} data-testid="story-eyebrow"/>
        </div>
        <div>
          <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Page title</label>
          <input className="w-full border border-line px-3 py-2 bg-pearl" value={content.title}
            onChange={(e) => setContent({ ...content, title: e.target.value })} data-testid="story-title"/>
        </div>
      </div>

      <div>
        <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Hero image (optional — shown under the title)</label>
        <ImageUploader
          value={content.hero_image_url ? [content.hero_image_url] : []}
          onChange={(urls) => setContent({ ...content, hero_image_url: urls[0] || "" })}
          multiple={false}
          maxFiles={1}
          kind="site"
        />
      </div>

      <div className="border-t border-line pt-4">
        <div className="eyebrow mb-3">Page Content</div>
        {content.blocks.length === 0 && (
          <div className="text-xs text-ash mb-3">No content yet — add a paragraph or image below.</div>
        )}
        <div className="space-y-3">
          {content.blocks.map((b, i) => (
            <div key={i} className="border border-line p-3 bg-pearl/40" data-testid={`story-block-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-widest text-maroon">{b.type}</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                    className="p-1 text-ash hover:text-maroon disabled:opacity-30" aria-label="Move up"><ArrowUp size={14}/></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === content.blocks.length - 1}
                    className="p-1 text-ash hover:text-maroon disabled:opacity-30" aria-label="Move down"><ArrowDown size={14}/></button>
                  <button type="button" onClick={() => removeBlock(i)}
                    className="p-1 text-ash hover:text-maroon" aria-label="Remove" data-testid={`story-block-remove-${i}`}><Trash2 size={14}/></button>
                </div>
              </div>

              {b.type === "image" ? (
                <div className="space-y-2">
                  <ImageUploader
                    value={b.image_url ? [b.image_url] : []}
                    onChange={(urls) => setBlock(i, { image_url: urls[0] || "" })}
                    multiple={false}
                    maxFiles={1}
                    kind="site"
                  />
                  <input className="w-full border border-line px-3 py-2 bg-white text-sm" placeholder="Caption (optional)"
                    value={b.caption} onChange={(e) => setBlock(i, { caption: e.target.value })}/>
                </div>
              ) : b.type === "heading" ? (
                <input className="w-full border border-line px-3 py-2 bg-white font-display text-lg" placeholder="Section heading"
                  value={b.text} onChange={(e) => setBlock(i, { text: e.target.value })}/>
              ) : (
                <textarea rows={4} className="w-full border border-line px-3 py-2 bg-white text-sm" placeholder="Paragraph text"
                  value={b.text} onChange={(e) => setBlock(i, { text: e.target.value })}/>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={() => addBlock("paragraph")} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="story-add-paragraph"><Plus size={12}/> Paragraph</button>
          <button type="button" onClick={() => addBlock("heading")} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="story-add-heading"><Plus size={12}/> Heading</button>
          <button type="button" onClick={() => addBlock("image")} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="story-add-image"><Plus size={12}/> Image</button>
        </div>
      </div>

      <button className="btn-maroon inline-flex items-center gap-2" disabled={saving} data-testid="story-save">
        <Save size={14}/> {saving ? "Saving…" : "Save Our Story Page"}
      </button>
    </form>
  );
}


/* ---------------- Home page "Save in Gold" ribbon ---------------- */
function HomeSchemeEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/crm/site-content/home-scheme")
      .then((r) => setC({ eyebrow: "", title: "", body: "", cta_label: "", cta_link: "", hero_image_url: "", ...(r.data || {}) }))
      .catch(() => setC({ eyebrow: "", title: "", body: "", cta_label: "", cta_link: "", hero_image_url: "" }));
  }, []);

  if (!c) return <div className="bg-white border border-line p-6 text-sm text-ash">Loading…</div>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/crm/site-content/home-scheme", {
        eyebrow: c.eyebrow || "", title: c.title || "", body: c.body || "",
        cta_label: c.cta_label || "", cta_link: c.cta_link || "",
        hero_image_url: c.hero_image_url || "", blocks: [],
      });
      toast.success("Home scheme section updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="bg-white border border-line p-6 space-y-4" data-testid="home-scheme-editor">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="text-maroon" />
        <h2 className="font-display text-2xl text-maroon">Home Scheme Section</h2>
      </div>
      <p className="text-[11px] text-ash -mt-2">
        The maroon "Save in Gold" band on the home page. Leave the title and text blank to hide the whole section.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Eyebrow (small text above title)</label>
          <input className="w-full border border-line px-3 py-2 bg-pearl" value={c.eyebrow}
            onChange={(e) => setC({ ...c, eyebrow: e.target.value })} data-testid="scheme-eyebrow"/>
        </div>
        <div>
          <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Title</label>
          <input className="w-full border border-line px-3 py-2 bg-pearl" value={c.title}
            onChange={(e) => setC({ ...c, title: e.target.value })} data-testid="scheme-title"/>
        </div>
      </div>

      <div>
        <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Description</label>
        <textarea rows={3} className="w-full border border-line px-3 py-2 bg-pearl" value={c.body}
          onChange={(e) => setC({ ...c, body: e.target.value })} data-testid="scheme-body"/>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Button text</label>
          <input className="w-full border border-line px-3 py-2 bg-pearl" value={c.cta_label}
            onChange={(e) => setC({ ...c, cta_label: e.target.value })} data-testid="scheme-cta-label"/>
        </div>
        <div>
          <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Button link</label>
          <input className="w-full border border-line px-3 py-2 bg-pearl" placeholder="/gold-programs" value={c.cta_link}
            onChange={(e) => setC({ ...c, cta_link: e.target.value })} data-testid="scheme-cta-link"/>
        </div>
      </div>

      <div>
        <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Section image</label>
        <ImageUploader
          value={c.hero_image_url ? [c.hero_image_url] : []}
          onChange={(urls) => setC({ ...c, hero_image_url: urls[0] || "" })}
          multiple={false} maxFiles={1} kind="site"
        />
      </div>

      <button className="btn-maroon inline-flex items-center gap-2" disabled={saving} data-testid="scheme-save">
        <Save size={14}/> {saving ? "Saving…" : "Save Section"}
      </button>
    </form>
  );
}


/* ---------------- WhatsApp quick questions ---------------- */
function WhatsAppQuestionsEditor() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState({ label: "", message: "" });

  const load = () => api.get("/api/crm/wa-questions").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!adding.label.trim()) { toast.error("Enter the question"); return; }
    try {
      await api.post("/api/crm/wa-questions", {
        label: adding.label.trim(),
        message: adding.message.trim(),
        order: (items.length + 1),
        active: true,
      });
      setAdding({ label: "", message: "" });
      toast.success("Question added");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add"); }
  };

  const patch = async (q, body) => {
    try { await api.patch(`/api/crm/wa-questions/${q.id}`, body); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not update"); }
  };

  const remove = async (q) => {
    try { await api.delete(`/api/crm/wa-questions/${q.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div className="bg-white border border-line p-6 space-y-4" data-testid="wa-questions-editor">
      <div className="flex items-center gap-2">
        <MessageCircle size={18} className="text-maroon" />
        <h2 className="font-display text-2xl text-maroon">WhatsApp Quick Questions</h2>
      </div>
      <p className="text-[11px] text-ash -mt-2">
        Shown when a visitor taps the WhatsApp bubble on the website. They pick one and it opens
        WhatsApp with that message ready to send. Visitors can also type their own question.
      </p>

      <div className="space-y-2">
        {items.map((q) => (
          <div key={q.id} className="border border-line p-3 bg-pearl/40" data-testid={`wa-q-row-${q.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <input
                defaultValue={q.label}
                onBlur={(e) => e.target.value !== q.label && patch(q, { label: e.target.value })}
                className="flex-1 min-w-[12rem] border border-line px-2 py-1.5 text-sm bg-white"
                placeholder="Question shown on the button"
              />
              <input
                type="number"
                defaultValue={q.order}
                onBlur={(e) => +e.target.value !== q.order && patch(q, { order: +e.target.value })}
                className="w-16 border border-line px-2 py-1.5 text-sm bg-white"
                title="Order"
              />
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={q.active !== false} onChange={() => patch(q, { active: !(q.active !== false) })}/>
                Active
              </label>
              <button onClick={() => remove(q)} className="p-1 text-ash hover:text-maroon" aria-label="Remove">
                <Trash2 size={14}/>
              </button>
            </div>
            <input
              defaultValue={q.message}
              onBlur={(e) => e.target.value !== q.message && patch(q, { message: e.target.value })}
              className="w-full border border-line px-2 py-1.5 text-xs bg-white mt-2"
              placeholder="Message pre-filled in WhatsApp (leave blank to use the question text)"
            />
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-ash">No questions yet — add one below.</div>}
      </div>

      <form onSubmit={add} className="border-t border-line pt-4 space-y-2">
        <div className="eyebrow text-[10px]">Add a question</div>
        <input
          className="w-full border border-line px-3 py-2 bg-pearl text-sm"
          placeholder="e.g. Do you offer EMI?"
          value={adding.label}
          onChange={(e) => setAdding({ ...adding, label: e.target.value })}
          data-testid="wa-q-new-label"
        />
        <input
          className="w-full border border-line px-3 py-2 bg-pearl text-sm"
          placeholder="WhatsApp message (optional)"
          value={adding.message}
          onChange={(e) => setAdding({ ...adding, message: e.target.value })}
          data-testid="wa-q-new-message"
        />
        <button className="btn-maroon inline-flex items-center gap-2 text-sm" data-testid="wa-q-add">
          <Plus size={14}/> Add Question
        </button>
      </form>
    </div>
  );
}
